import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import RideTracking from "./pages/RideTracking";
import Airdrops from "./pages/Airdrops";
import Challenges from "./pages/Challenges";
import Shop from "./pages/Shop";
import CreateListing from "./pages/CreateListing";
import Leaderboard from "./pages/Leaderboard";
import Profile from "./pages/Profile";
import Wallet from "./pages/Wallet";
import AdminDashboard from "./pages/AdminDashboard";
import VideoPreview from "./pages/VideoPreview";
import Achievements from "./pages/Achievements";
import Garage from "./pages/Garage";
import Social from "./pages/Social";
import RideHistory from "./pages/RideHistory";
import Messages from "./pages/Messages";
import HelpCenter from "./pages/HelpCenter";
import ControllerBay from "./pages/ControllerBay";
import Governance from "./pages/Governance";
import ARMode from "./pages/ARMode";
import Subscription from "./pages/Subscription";
import Community from "./pages/Community";
import ARWaypoints from "./pages/ARWaypoints";
import RideReplay from "./pages/RideReplay";
import StripeConnect from "./pages/StripeConnect";
import ProductDetail from "./pages/ProductDetail";
import OrderHistory from "./pages/OrderHistory";
import Wishlist from "./pages/Wishlist";
import Referrals from "./pages/Referrals";
import PriceAlerts from "./pages/PriceAlerts";
import Lookbook from "./pages/Lookbook";
import Cart from "./pages/Cart";
import SellerAnalytics from "./pages/SellerAnalytics";
import Chat from "./pages/Chat";
import Badges from "./pages/Badges";
import AdminAnalytics from "./pages/AdminAnalytics";
import Billing from "./pages/Billing";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import { Privacy, Terms } from "./pages/Legal";

function AppRouter() {
    const location = useLocation();
    
    // Check URL fragment for session_id - handle OAuth callback FIRST
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
            
            {/* Protected Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/ride" 
                element={
                    <ProtectedRoute>
                        <RideTracking />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/airdrops" 
                element={
                    <ProtectedRoute>
                        <Airdrops />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/challenges" 
                element={
                    <ProtectedRoute>
                        <Challenges />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/shop" 
                element={
                    <ProtectedRoute>
                        <Shop />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/listings/create" 
                element={
                    <ProtectedRoute>
                        <CreateListing />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/leaderboard" 
                element={
                    <ProtectedRoute>
                        <Leaderboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/wallet" 
                element={
                    <ProtectedRoute>
                        <Wallet />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin" 
                element={
                    <AdminRoute>
                        <AdminDashboard />
                    </AdminRoute>
                } 
            />
            <Route 
                path="/videos" 
                element={
                    <ProtectedRoute>
                        <VideoPreview />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/achievements" 
                element={
                    <ProtectedRoute>
                        <Achievements />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/garage" 
                element={
                    <ProtectedRoute>
                        <Garage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/social" 
                element={
                    <ProtectedRoute>
                        <Social />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/ride-history" 
                element={<ProtectedRoute><RideHistory /></ProtectedRoute>} 
            />
            <Route 
                path="/messages" 
                element={<ProtectedRoute><Messages /></ProtectedRoute>} 
            />
            <Route 
                path="/help" 
                element={<HelpCenter />} 
            />
            <Route 
                path="/controller" 
                element={<ProtectedRoute><ControllerBay /></ProtectedRoute>} 
            />
            <Route 
                path="/governance" 
                element={<ProtectedRoute><Governance /></ProtectedRoute>} 
            />
            <Route 
                path="/ar" 
                element={<ProtectedRoute><ARMode /></ProtectedRoute>} 
            />
            <Route 
                path="/subscription" 
                element={<ProtectedRoute><Subscription /></ProtectedRoute>} 
            />
            <Route 
                path="/community" 
                element={<ProtectedRoute><Community /></ProtectedRoute>} 
            />
            <Route 
                path="/ar-waypoints" 
                element={<ProtectedRoute><ARWaypoints /></ProtectedRoute>} 
            />
            <Route 
                path="/ride-replay/:rideId" 
                element={<ProtectedRoute><RideReplay /></ProtectedRoute>} 
            />
            <Route 
                path="/connect" 
                element={<ProtectedRoute><StripeConnect /></ProtectedRoute>} 
            />
            <Route 
                path="/shop/item/:itemId" 
                element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} 
            />
            <Route 
                path="/orders" 
                element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} 
            />
            <Route 
                path="/wishlist" 
                element={<ProtectedRoute><Wishlist /></ProtectedRoute>} 
            />
            <Route 
                path="/referrals" 
                element={<ProtectedRoute><Referrals /></ProtectedRoute>} 
            />
            <Route 
                path="/alerts" 
                element={<ProtectedRoute><PriceAlerts /></ProtectedRoute>} 
            />
            <Route 
                path="/lookbook" 
                element={<ProtectedRoute><Lookbook /></ProtectedRoute>} 
            />
            <Route 
                path="/cart" 
                element={<ProtectedRoute><Cart /></ProtectedRoute>} 
            />
            <Route 
                path="/seller-analytics" 
                element={<ProtectedRoute><SellerAnalytics /></ProtectedRoute>} 
            />
            <Route 
                path="/chat" 
                element={<ProtectedRoute><Chat /></ProtectedRoute>} 
            />
            <Route 
                path="/badges" 
                element={<ProtectedRoute><Badges /></ProtectedRoute>} 
            />
            <Route 
                path="/admin-analytics" 
                element={<AdminRoute><AdminAnalytics /></AdminRoute>} 
            />
            <Route 
                path="/billing" 
                element={<ProtectedRoute><Billing /></ProtectedRoute>} 
            />
            
            {/* Fallback */}
            <Route path="*" element={<LandingPage />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <AppRouter />
                    <Toaster position="top-center" richColors />
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
